import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { AnalyticsOperationDto, AnalyticsOperationsResponseDto } from "@normax-performance/shared-types";
import { SortableColumnHeader, useSortableRows } from "../../../shared-ui/primitives";
import { useLocale } from "../../../i18n/LocaleProvider";
import { formatAnalyticsMinutes, formatAnalyticsPercent } from "../../../lib/format";

export interface OperationsTableProps {
  response: AnalyticsOperationsResponseDto;
}

/** Operation analytics — same design language as ProductsTable (docs/.../FORENSIC_PRODUCT_BRIEF.md §9, this slice's spec §16), no quantity aggregate. */
export function OperationsTable({ response }: OperationsTableProps) {
  const { t } = useTranslation("analytics");
  const { intlLocale } = useLocale();

  // "Standardni min" (earned standard minutes) removed — Method C intermediate,
  // not business-facing (task: time-distribution simplification §7).
  const columns = {
    code: (o: AnalyticsOperationDto) => o.code,
    name: (o: AnalyticsOperationDto) => o.name,
    normedMinutes: (o: AnalyticsOperationDto) => Number(o.normedMinutes),
    performance: (o: AnalyticsOperationDto) => (o.performancePercentLabel === null ? null : Number(o.performancePercentLabel)),
    workers: (o: AnalyticsOperationDto) => o.workerCount,
    products: (o: AnalyticsOperationDto) => o.productCount,
  };
  const { sorted, sortKey, direction, requestSort } = useSortableRows(response.operations, columns, {
    key: "normedMinutes",
    direction: "desc",
  });

  return (
    <div className="nx-analytics-table-wrap nx-scroll-table">
      <table className="nx-analytics-table nx-table">
        <thead>
          <tr>
            <SortableColumnHeader label={t("operations.code")} sortKey="code" activeSortKey={sortKey} direction={direction} onSort={requestSort} />
            <SortableColumnHeader label={t("operations.operation")} sortKey="name" activeSortKey={sortKey} direction={direction} onSort={requestSort} />
            <SortableColumnHeader label={t("operations.normedMinutes")} sortKey="normedMinutes" activeSortKey={sortKey} direction={direction} onSort={requestSort} className="nx-analytics-table__num" />
            <SortableColumnHeader label={t("operations.performance")} sortKey="performance" activeSortKey={sortKey} direction={direction} onSort={requestSort} className="nx-analytics-table__num" />
            <SortableColumnHeader label={t("operations.workers")} sortKey="workers" activeSortKey={sortKey} direction={direction} onSort={requestSort} className="nx-analytics-table__num" />
            <SortableColumnHeader label={t("operations.products")} sortKey="products" activeSortKey={sortKey} direction={direction} onSort={requestSort} className="nx-analytics-table__num" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((o) => (
            <tr key={o.operationId}>
              <td>{o.code ?? "—"}</td>
              <td>
                {o.operationId ? (
                  <Link to={`/izvestaji?type=OPERATION&operationId=${o.operationId}&from=${response.period.from}&to=${response.period.to}`}>{o.name}</Link>
                ) : (
                  o.name
                )}
              </td>
              <td className="nx-analytics-table__num">{formatAnalyticsMinutes(o.normedMinutes, intlLocale)}</td>
              <td className="nx-analytics-table__num">
                <span className={`nx-analytics-table__cell-readout${o.zone ? ` nx-analytics-table__cell-readout--${o.zone.zone}` : ""}`}>
                  {formatAnalyticsPercent(o.performancePercentLabel, intlLocale)}
                </span>
              </td>
              <td className="nx-analytics-table__num">{o.workerCount}</td>
              <td className="nx-analytics-table__num">{o.productCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
